import type { AttendanceItem } from "views/Attendance/types"
import type { RequestLeaveItem } from "views/RequestLeave/types"

import { RequestLeaveAction } from "__generated__/graphql"
import {
  Brightness1Sharp,
  CrisisAlert,
  Logout,
  PendingActions,
  Restore,
  SettingsRemote,
  Update,
  WatchLaterOutlined,
} from "@mui/icons-material"
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  Flex,
  FlexColumn,
  getDataViewStickyColumnStyles,
  getDataViewTableStyles,
  GridEmpty,
  LoadingProgress,
  useDataViewTableScroll,
} from "components/common"
import { DateTime } from "luxon"
import { ATTENDANCE_LAYOUT, getAttendanceTableGridColumns, getAttendanceTableMinWidth } from "views/Attendance/styles"
import {
  calculateWorkingTime,
  getAttendanceActionTime,
  getGroupedByUsers,
  getMissingCheckoutCount,
  hasLateArrivalRequest,
  isCheckInAction,
  isCheckinLate,
  sortAttendancesByActionTime,
} from "views/Attendance/utils"
import { REQUEST_ACTION_LABELS } from "views/RequestLeave/constants"

type AttendanceGroup = ReturnType<typeof getGroupedByUsers>[number]

export type AttendanceDetailSelection = {
  attendances: AttendanceItem[]
  attendeeName?: null | string
  date: DateTime
  leaves: RequestLeaveItem[]
}

type Props = {
  dateRange: DateTime[]
  groups: AttendanceGroup[]
  hasError: boolean
  leavesByAttendeeDate: Map<string, RequestLeaveItem[]>
  loading: boolean
  onSelectDetail: (detail: AttendanceDetailSelection) => void
  showCheckins: boolean
  showOvertime: boolean
}

const getRequestNoteIcon = (action: RequestLeaveAction) => {
  switch (action) {
    case RequestLeaveAction.RequestLate:
      return <Restore color="warning" fontSize="small" />
    case RequestLeaveAction.RequestLeave:
      return <Logout color="error" fontSize="small" />
    case RequestLeaveAction.RequestLeaveEarly:
      return <Update color="warning" fontSize="small" />
    case RequestLeaveAction.RequestRemote:
      return <SettingsRemote color="info" fontSize="small" />
    default:
      return null
  }
}

const AttendanceTable = ({
  dateRange,
  groups,
  hasError,
  leavesByAttendeeDate,
  loading,
  onSelectDetail,
  showCheckins,
  showOvertime,
}: Props) => {
  const { onBodyScroll, tableHeadRef } = useDataViewTableScroll()
  const tableGridColumns = getAttendanceTableGridColumns(dateRange.length)
  const tableMinWidth = getAttendanceTableMinWidth(dateRange.length)

  return (
    <TableContainer
      className="relative"
      component={Paper}
      sx={{
        borderRadius: 2,
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
      variant="outlined"
    >
      <Table
        sx={(theme) =>
          getDataViewTableStyles(theme, {
            gridTemplateColumns: tableGridColumns,
            minWidth: tableMinWidth,
          })
        }
      >
        <TableHead ref={tableHeadRef}>
          <TableRow>
            <TableCell
              align="center"
              sx={(theme) => ({
                ...getDataViewStickyColumnStyles(theme, "left"),
                left: 0,
                position: "sticky",
                zIndex: 3,
              })}
            >
              Nhân viên
            </TableCell>
            {dateRange.map((date) => (
              <TableCell align="center" key={date.toISODate()}>
                <Box textAlign="center">
                  <Typography fontWeight={700} variant="subtitle2">
                    {date.toFormat("dd/MM/yyyy")}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {date.setLocale("vi").toFormat("cccc")}
                  </Typography>
                </Box>
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={(theme) => ({
                ...getDataViewStickyColumnStyles(theme, "right"),
                position: "sticky",
                right: 0,
                zIndex: 3,
              })}
            >
              Tổng kết
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody onScroll={onBodyScroll}>
          {loading && (
            <TableRow sx={{ gridTemplateColumns: "1fr !important" }}>
              <TableCell>
                <LoadingProgress loading />
              </TableCell>
            </TableRow>
          )}

          {!hasError && !loading && groups.length === 0 && (
            <TableRow sx={{ gridTemplateColumns: "1fr !important" }}>
              <TableCell>
                <GridEmpty />
              </TableCell>
            </TableRow>
          )}

          {groups.map((group) => {
            const isPartTime = Boolean(group.attendee.isPartTime)
            let lateCount = 0
            let missingCheckoutCount = 0
            let workingCount = 0

            return (
              <TableRow hover key={group.id} sx={{ minHeight: ATTENDANCE_LAYOUT.tableRowMinHeight }}>
                <TableCell
                  align="center"
                  sx={(theme) => ({
                    ...getDataViewStickyColumnStyles(theme, "left"),
                    left: 0,
                    position: "sticky",
                    zIndex: 1,
                  })}
                >
                  <FlexColumn alignItems="center" gap={0.5}>
                    {isPartTime && <PendingActions color="warning" fontSize="small" />}
                    <Typography color="primary.main" fontWeight={700} variant="body2">
                      {group.attendee.name}
                    </Typography>
                    {isPartTime && <Chip color="warning" label="Part time" size="small" variant="outlined" />}
                  </FlexColumn>
                </TableCell>

                {dateRange.map((date) => {
                  const dateKey = date.toFormat("yyyy-MM-dd")
                  const dayAttendances = sortAttendancesByActionTime(
                    group.attendances.filter((attendance) => attendance.date === dateKey),
                  )
                  const dayLeaves = leavesByAttendeeDate.get(`${group.attendee.id}-${dateKey}`) ?? []
                  const isLateDay = date.weekday < 6 && date < DateTime.now().endOf("day")
                  const hasLateRequest = hasLateArrivalRequest(dayLeaves)
                  const isLate = isLateDay && !isPartTime && showCheckins && isCheckinLate(dayAttendances)
                  if ((isLateDay && !isPartTime && hasLateRequest) || isLate) lateCount += 1

                  const workingMinutes = calculateWorkingTime(dayAttendances)
                  missingCheckoutCount += getMissingCheckoutCount(dayAttendances)
                  workingCount += workingMinutes

                  const handleOpenDetail = () => {
                    onSelectDetail({
                      attendances: dayAttendances,
                      attendeeName: group.attendee.name,
                      date,
                      leaves: dayLeaves,
                    })
                  }

                  return (
                    <TableCell
                      align="center"
                      aria-label={`Xem chi tiết ${group.attendee.name ?? "nhân viên"} ngày ${date.toFormat(
                        "dd/MM/yyyy",
                      )}`}
                      key={dateKey}
                      onClick={handleOpenDetail}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return
                        event.preventDefault()
                        handleOpenDetail()
                      }}
                      role="button"
                      sx={{ "&:hover": { bgcolor: "action.hover" }, cursor: "pointer", overflow: "hidden" }}
                      tabIndex={0}
                    >
                      <FlexColumn alignItems="center" gap={0.75}>
                        {!isPartTime && (
                          <Flex alignItems="center" gap={0.5} minHeight={24}>
                            {isLate && (
                              <Tooltip arrow title="Đi làm muộn">
                                <CrisisAlert color="error" fontSize="small" />
                              </Tooltip>
                            )}
                            {dayLeaves.slice(0, 2).map((leave) => (
                              <Tooltip
                                arrow
                                key={leave.id}
                                title={`${REQUEST_ACTION_LABELS[leave.action]}: ${leave.reason}`}
                              >
                                <Box lineHeight={0}>{getRequestNoteIcon(leave.action)}</Box>
                              </Tooltip>
                            ))}
                            {dayLeaves.length > 2 && (
                              <Typography color="text.secondary" variant="caption">
                                +{dayLeaves.length - 2}
                              </Typography>
                            )}
                          </Flex>
                        )}

                        <FlexColumn alignItems="flex-start" gap={0.5}>
                          {dayAttendances.map((attendance) => {
                            const isCheckIn = isCheckInAction(attendance.action)
                            return (
                              <Flex alignItems="center" gap={0.5} key={attendance.id} title={attendance.action}>
                                <Brightness1Sharp
                                  color={isCheckIn ? "success" : "error"}
                                  sx={(theme) => ({ fontSize: theme.spacing(1.25) })}
                                />
                                <Typography variant="body2" whiteSpace="nowrap">
                                  {getAttendanceActionTime(attendance).toFormat("HH:mm")}
                                </Typography>
                              </Flex>
                            )
                          })}
                        </FlexColumn>

                        {dayAttendances.length > 0 && (
                          <Flex alignItems="center" gap={0.5}>
                            <WatchLaterOutlined color="warning" fontSize="small" />
                            <Typography variant="body2">
                              {workingMinutes === 0 ? "—" : `${(workingMinutes / 60).toFixed(2)} giờ`}
                            </Typography>
                          </Flex>
                        )}
                      </FlexColumn>
                    </TableCell>
                  )
                })}

                <TableCell
                  align="center"
                  sx={(theme) => ({
                    ...getDataViewStickyColumnStyles(theme, "right"),
                    position: "sticky",
                    right: 0,
                    zIndex: 1,
                  })}
                >
                  <FlexColumn alignItems="center" gap={0.75}>
                    <Chip
                      color={isPartTime || lateCount <= 4 ? "success" : "error"}
                      label={`Đi muộn: ${lateCount} lần`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      color={missingCheckoutCount > 0 ? "warning" : "default"}
                      label={`Chưa checkout: ${missingCheckoutCount}`}
                      size="small"
                      variant="outlined"
                    />
                    <Flex alignItems="center" gap={0.5} justifyContent="center">
                      <WatchLaterOutlined color="primary" fontSize="small" />
                      <Typography color="primary.main" fontWeight={700} variant="body2">
                        {(workingCount / 60).toFixed(2)} giờ
                      </Typography>
                    </Flex>
                    {showOvertime && !showCheckins && (
                      <Typography color="text.secondary" variant="caption">
                        ≈ {(workingCount / 60 / 8).toFixed(2)} ngày
                      </Typography>
                    )}
                  </FlexColumn>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default AttendanceTable
